import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { getTotalAmount, getTotalAmountCounterOffer, postPendingApplication, createApplication } from 'api';
import { setDirectToDayEnd } from 'utils/daysjsConfig';
import type { ITenor } from 'interfaces/activeApllication';
import type { ProductConditionsType, INewApplication } from 'interfaces';
import { updateUserAction } from './user';
import {
  removeRegTypeItem,
  GARegisterEvent,
  getLabelGAConfirmAndSign,
  collectFingerprintProFields,
  clearFingerprintProCache,
  normalizePromoCode,
} from 'utils';

export interface CalculationProduct extends ProductConditionsType {
  realMaturityDate?: string;
  maturityDateByPlan?: string;
}

export interface ICalculationState {
  principal: number | null;
  tenor: number | null;
  promoCode: string | undefined;
  total: number | undefined;
  isInitialized: boolean;
  product: CalculationProduct | null;
  isDateDifferent: boolean;
  promocodeError: string | null;
  appliedPromoCode: string | undefined;
  /** Incremented when getTotalThunk settles (fulfilled or rejected). Used by UI to clear promo apply/remove loading. */
  getTotalSettledCounter: number;
  /** When true, use PATCH /amounts for counter offer flow. */
  isCounterOffer: boolean;
}

interface GetTotalParams {
  principal: number;
  tenor: number;
  promoCode?: string | null;
  tenors: ITenor[];
}

interface InitCalculationParams {
  defaultPrincipal: number;
  defaultTenor: number;
  tenors: ITenor[];
  /** Pending data from page (AutoApplyAlert). When provided, init does NOT call getPendingApplication. */
  pending?: { principal?: number; tenor?: number; promo_code?: string } | null;
}

interface SubmitCalculationParams {
  bankAccountUuid?: string;
  isInAccount?: boolean;
}

const initialState: ICalculationState = {
  principal: null,
  tenor: null,
  promoCode: undefined,
  total: undefined,
  isInitialized: false,
  product: null,
  isDateDifferent: false,
  promocodeError: null,
  appliedPromoCode: undefined,
  getTotalSettledCounter: 0,
  isCounterOffer: false,
};

export const calculationSlice = createSlice({
  name: 'calculation',
  initialState,
  reducers: {
    setCalculation: (state, action: PayloadAction<{ principal: number; tenor: number; promoCode?: string }>) => {
      const { principal, tenor, promoCode } = action.payload;
      const normalizedPromoCode = normalizePromoCode(promoCode);
      state.principal = principal;
      state.tenor = tenor;
      state.promoCode = normalizedPromoCode;
      state.promocodeError = null;
      if (!normalizedPromoCode) {
        state.appliedPromoCode = undefined;
      }
    },
    updateCalculation: (
      state,
      action: PayloadAction<{
        principal: number;
        tenor: number;
        promoCode?: string | null;
        tenors?: ITenor[];
      }>,
    ) => {
      const { principal, tenor, promoCode } = action.payload;
      state.principal = principal;
      state.tenor = tenor;
      if (promoCode !== undefined) {
        const normalizedPromoCode = normalizePromoCode(promoCode);
        state.promoCode = normalizedPromoCode;
        state.promocodeError = null;
        if (!normalizedPromoCode) {
          state.appliedPromoCode = undefined;
        }
      }
    },
    setCalculationResult: (
      state,
      action: PayloadAction<{
        total: number;
        product: CalculationProduct;
        isDateDifferent: boolean;
        appliedPromoCode?: string;
      }>,
    ) => {
      state.total = action.payload.total;
      state.product = action.payload.product;
      state.isDateDifferent = action.payload.isDateDifferent;
      if ('appliedPromoCode' in action.payload) {
        state.appliedPromoCode = normalizePromoCode(action.payload.appliedPromoCode);
      }
      state.promocodeError = null;
    },
    setIsInitialized: (state, action: PayloadAction<boolean>) => {
      state.isInitialized = action.payload;
    },
    setPromocodeError: (state, action: PayloadAction<string | null>) => {
      state.promocodeError = action.payload;
    },
    clearPromocodeError: state => {
      state.promocodeError = null;
    },
    getTotalSettled: state => {
      state.getTotalSettledCounter = (state.getTotalSettledCounter ?? 0) + 1;
    },
    setIsCounterOffer: (state, action: PayloadAction<boolean>) => {
      state.isCounterOffer = action.payload;
    },
    cleanupCalculation: () => initialState,
  },
});

export const {
  setCalculation,
  updateCalculation,
  setCalculationResult,
  setIsInitialized,
  setPromocodeError,
  getTotalSettled,
  setIsCounterOffer,
  cleanupCalculation,
} = calculationSlice.actions;

export const selectCalculation = (state: RootState) => state.calculation;

export const selectCalculationInput = (state: RootState) => ({
  principal: state.calculation.principal,
  tenor: state.calculation.tenor,
  promoCode: state.calculation.promoCode,
});

export const selectIsCalculationInitialized = (state: RootState) => state.calculation.isInitialized;

export const getTotalThunk = createAsyncThunk<
  undefined,
  GetTotalParams,
  { state: RootState; rejectValue: { detail?: string; code?: string } }
>('calculation/getTotal', async ({ principal, tenor, promoCode, tenors }, { dispatch, getState, rejectWithValue }) => {
  const tenorItem: ITenor | undefined = tenors.find((item: ITenor) => item.tenor === tenor);
  if (!tenorItem) {
    return undefined;
  }
  const realPaymentDate = setDirectToDayEnd(tenorItem.real_maturity_date).format();
  const { userInfo } = getState().user;

  const finalPromoCode = normalizePromoCode(promoCode);

  const payload: Record<string, unknown> = {
    principal,
    tenor,
    payment_date: realPaymentDate,
  };
  if (finalPromoCode) {
    payload.promo_code = finalPromoCode;
  }

  try {
    const { isCounterOffer } = getState().calculation;
    const fetchTotalAmount = isCounterOffer ? getTotalAmountCounterOffer : getTotalAmount;
    const data = await fetchTotalAmount(payload);
    const product: CalculationProduct = {
      realMaturityDate: tenorItem.real_maturity_date,
      maturityDateByPlan: tenorItem.maturity_date_by_plan,
      ...data,
    };
    const isDateDifferent = tenorItem.real_maturity_date !== tenorItem.maturity_date_by_plan;

    dispatch(
      setCalculationResult({
        total: data.amount_total,
        product,
        isDateDifferent,
        appliedPromoCode: finalPromoCode,
      }),
    );
    dispatch(
      updateUserAction({
        user: {
          loan_amount: principal,
          loan_tenor: tenor,
          loan_total_amount: data.amount_total,
          loan_real_maturity_date: data.real_maturity_date,
          promo_code: finalPromoCode ?? normalizePromoCode(userInfo?.promo_code),
        },
      }),
    );
  } catch (error: unknown) {
    const data = (error as { response?: { data?: { detail?: string; code?: string } } })?.response?.data;
    const message = data?.detail ?? 'Código promocional inválido o vencido';
    dispatch(calculationSlice.actions.setPromocodeError(message));
    return rejectWithValue({ detail: message, code: data?.code });
  }
  return undefined;
});

export const saveDraftThunk = createAsyncThunk<undefined, undefined, { state: RootState }>(
  'calculation/saveDraft',
  async (_, { getState }) => {
    const { calculation, user } = getState();
    const { principal, tenor, appliedPromoCode } = calculation;
    const isNew = !user.userInfo?.has_closed_loans;
    if (!isNew || principal == null || tenor == null) {
      return undefined;
    }
    const fingerprintFields = await collectFingerprintProFields();
    const payload: {
      principal: number;
      tenor: number;
      promo_code?: string;
      fingerprint_request_id?: string;
      fingerprint_visitor_id?: string;
    } = { principal, tenor, ...fingerprintFields };
    const normalizedPromoCode = normalizePromoCode(appliedPromoCode);
    if (normalizedPromoCode) {
      payload.promo_code = normalizedPromoCode;
    }
    await postPendingApplication(payload);
    return undefined;
  },
);

export const initCalculationThunk = createAsyncThunk<undefined, InitCalculationParams, { state: RootState }>(
  'calculation/init',
  async ({ defaultPrincipal, defaultTenor, tenors, pending }, { dispatch }) => {
    let principal = defaultPrincipal;
    let tenor = defaultTenor;
    let promoCode: string | undefined;
    if (pending != null && typeof pending === 'object') {
      if (pending.principal != null) principal = Number(pending.principal);
      if (pending.tenor != null) tenor = Number(pending.tenor);
      promoCode = normalizePromoCode(pending.promo_code);
    }
    dispatch(setCalculation({ principal, tenor, promoCode }));
    dispatch(setIsInitialized(true));
    if (tenors.length > 0) {
      await dispatch(getTotalThunk({ principal, tenor, promoCode: promoCode ?? null, tenors })).unwrap();
    }
    return undefined;
  },
);

export const submitCalculationThunk = createAsyncThunk<
  { uuid: string; isFirstAppl?: boolean; isInAccount?: boolean },
  SubmitCalculationParams,
  { state: RootState }
>('calculation/submit', async ({ bankAccountUuid, isInAccount }, { getState, rejectWithValue }) => {
  const { calculation, user } = getState();
  const { principal, tenor, appliedPromoCode } = calculation;
  const { autoApplyTimerFinished } = getState().ui;

  if (principal == null || tenor == null) {
    return rejectWithValue('Missing principal or tenor');
  }

  const fingerprintFields = await collectFingerprintProFields();

  const normalizedPromoCode = normalizePromoCode(appliedPromoCode);

  const payload: INewApplication & { is_auto_submit?: boolean } = {
    principal,
    tenor,
    ...(bankAccountUuid ? { bank_account_uuid: bankAccountUuid } : {}),
    ...(normalizedPromoCode ? { promo_code: normalizedPromoCode } : {}),
    ...(autoApplyTimerFinished ? { is_auto_submit: autoApplyTimerFinished } : {}),
    ...fingerprintFields,
  };

  const response = await createApplication(payload as Parameters<typeof createApplication>[0]);
  clearFingerprintProCache();

  const userInfo = user.userInfo;
  const label = getLabelGAConfirmAndSign(userInfo);
  const isFirstAppl = label === 'sign_up';
  if (isFirstAppl) {
    removeRegTypeItem();
  }

  GARegisterEvent({
    event: 'confirmation_conditions',
    label,
    UID: userInfo.user_uuid,
    application_id: response.uuid,
    ...(isFirstAppl && userInfo.registration_type ? { registration: userInfo.registration_type } : {}),
  });

  return { uuid: response.uuid, isFirstAppl: isFirstAppl ?? false, isInAccount };
});

export default calculationSlice.reducer;
